import { useMemo, useState, useEffect } from "react"
import { useStore } from "@nanostores/react"
import { $allPools } from "../stores/pools-store"
import { $tokenInfoCache } from "../stores/token-info-store"
import { $poolsOverviewMap } from "../stores/pools-store"
import { PRE_CACHED_TOKENS } from "@/hooks/use-token-info"
import type { TokenInfo } from "../api/token-api"
import { isAddress } from "@/utils/format"
import type { Pool } from "@/api/pools-api"

export interface SearchResultsExactMatch {
  type: "pool" | "token" | "entity"
  data: Pool | { token: TokenInfo; pools: { id: string }[] } | string
}

export interface SearchResultsOptions {
  pools: Pool[]
  tokens: TokenInfo[]
  poolIds: string[]
  tokenIds: string[]
}

export interface SearchResultsGroupedPool {
  tokenIds: string[]
  tokenTickers: string[]
  pools: Pool[]
  totalLiquidity: number
}

export interface SearchResults {
  options: SearchResultsOptions
  groupedPools: Record<string, SearchResultsGroupedPool>
  loading: boolean
  exactMatch: SearchResultsExactMatch | null
}

export function useSearchResults(currentValue: string) {
  // Get pools and overview data from stores
  const allPools = useStore($allPools)
  const poolsOverview = useStore($poolsOverviewMap)

  // Check if it's an Arweave address
  const isArweaveAddress = useMemo(() => {
    return isAddress(currentValue)
  }, [currentValue])

  // Better loading state: start loading when hook runs then clear after processing.
  const [searchLoading, setSearchLoading] = useState(true)

  useEffect(() => {
    setSearchLoading(true)
    // In a real-world scenario, this might be asynchronous;
    // here we simulate a micro-task delay.
    const timer = setTimeout(() => setSearchLoading(false), 0)
    return () => clearTimeout(timer)
  }, [allPools, currentValue, isArweaveAddress])

  // Pre-calculate lower-case trimmed query for consistency.
  const lowerQuery = currentValue.trim().toLowerCase()

  // Compute fuzzy options (tokens and pools) based on the currentValue.
  const options = useMemo(() => {
    const tokenInfoCache = $tokenInfoCache.get()

    if (allPools.length === 0 || !currentValue) {
      return { pools: [], tokens: [], poolIds: [], tokenIds: [] }
    }

    // Merge tokens from pre-cached and cached values.
    const tokenCache = $tokenInfoCache.get()
    const tokenList = [
      ...Object.values(PRE_CACHED_TOKENS),
      ...Object.values(tokenCache)
        .map((v) => {
          try {
            return JSON.parse(v as string)
          } catch {
            return null
          }
        })
        .filter((t) => t),
    ]

    // Filter pools based on multiple criteria.
    const pools = allPools.filter((pool) => {
      try {
        const baseToken: TokenInfo =
          pool.baseToken in PRE_CACHED_TOKENS
            ? PRE_CACHED_TOKENS[pool.baseToken]
            : JSON.parse(tokenInfoCache[pool.baseToken] as string)
        const quoteToken: TokenInfo =
          pool.quoteToken in PRE_CACHED_TOKENS
            ? PRE_CACHED_TOKENS[pool.quoteToken]
            : JSON.parse(tokenInfoCache[pool.quoteToken] as string)

        const queryTrim = currentValue.trim()

        // Handle exact token pair match (e.g., "AO/QAR").
        if (queryTrim.includes("/")) {
          const tokens = queryTrim.split("/").map((t) => t.trim().toLowerCase())
          if (tokens.length === 2) {
            const [tokenA, tokenB] = tokens
            const matchPair =
              (baseToken.ticker.toLowerCase() === tokenA &&
                quoteToken.ticker.toLowerCase() === tokenB) ||
              (baseToken.ticker.toLowerCase() === tokenB &&
                quoteToken.ticker.toLowerCase() === tokenA)
            if (matchPair) return true
          }
        }

        // Exact token id match.
        if (
          baseToken.id.toLowerCase() === lowerQuery ||
          quoteToken.id.toLowerCase() === lowerQuery
        ) {
          return true
        }

        // Partial match on token name or ticker.
        if (
          baseToken.name.toLowerCase().includes(lowerQuery) ||
          baseToken.ticker.toLowerCase().includes(lowerQuery) ||
          quoteToken.name.toLowerCase().includes(lowerQuery) ||
          quoteToken.ticker.toLowerCase().includes(lowerQuery)
        ) {
          return true
        }

        // Direct pool id match.
        if (pool.id.toLowerCase() === lowerQuery) return true

        return false
      } catch {
        return false
      }
    })

    // Sort pools by a defined category.
    const sortedPools = pools.slice().sort((a, b) => {
      function getCategory(pool: Pool): number {
        try {
          const baseToken: TokenInfo =
            pool.baseToken in PRE_CACHED_TOKENS
              ? PRE_CACHED_TOKENS[pool.baseToken]
              : JSON.parse($tokenInfoCache.get()[pool.baseToken] as string)
          const quoteToken: TokenInfo =
            pool.quoteToken in PRE_CACHED_TOKENS
              ? PRE_CACHED_TOKENS[pool.quoteToken]
              : JSON.parse($tokenInfoCache.get()[pool.quoteToken] as string)

          const tickerMatch =
            baseToken.ticker.toLowerCase() === lowerQuery ||
            quoteToken.ticker.toLowerCase() === lowerQuery
          const isPublic = Boolean(pool.amm_status === "public")

          if (tickerMatch && isPublic) return 0
          if (!tickerMatch && isPublic) return 2
          if (tickerMatch && !isPublic) return 1
          return 3
        } catch {
          return 3
        }
      }
      return getCategory(a) - getCategory(b)
    })

    // Filter tokens based on the query.
    const tokenMatches = tokenList.filter((token) => {
      if (!token) return false
      // Exclude tokens with tickers starting with "bot".
      if (token.ticker.toLowerCase().startsWith("bot")) return false
      return token.ticker.toLowerCase().includes(lowerQuery)
    })
    const sortedTokens = tokenMatches.slice().sort((a, b) => a.ticker.length - b.ticker.length)
    const poolIds = pools.map((pool) => pool.id)
    const tokenIds = tokenMatches.map((token) => token.id)

    return {
      pools: sortedPools,
      tokens: sortedTokens,
      poolIds,
      tokenIds,
    }
  }, [allPools, currentValue, lowerQuery])

  // Determine an exact match (for a pool or token).
  const exactMatch = useMemo(() => {
    if (!currentValue) return null

    // Look for an exact pool match.
    const exactPool = options.pools.find((pool) => pool.id.toLowerCase() === lowerQuery)
    if (exactPool) {
      return { type: "pool", data: exactPool }
    }

    // Look for an exact token match.
    const exactToken = options.tokens.find(
      (token) => token.id.toLowerCase() === lowerQuery || token.ticker.toLowerCase() === lowerQuery,
    )
    if (exactToken) {
      // If token is an exact match, find all pools that contain this token.
      const relatedPools = options.pools.filter(
        (pool) => pool.baseToken === exactToken.id || pool.quoteToken === exactToken.id,
      )
      return { type: "token", data: { token: exactToken, pools: relatedPools } }
    }

    if (isArweaveAddress) {
      return { type: "entity", data: currentValue }
    }

    return null
  }, [options, currentValue, lowerQuery, isArweaveAddress])

  // If isArweaveAddress is true, override options to return only exact matches.
  const finalOptions = useMemo(() => {
    if (isArweaveAddress) {
      if (exactMatch) {
        if (exactMatch.type === "pool") {
          return {
            pools: [exactMatch.data],
            tokens: [] as TokenInfo[],
            poolIds: [(exactMatch.data as { id: string }).id],
            tokenIds: [] as string[],
          }
        }

        if (exactMatch.type === "token") {
          const { token, pools: tokenPools } = exactMatch.data as {
            token: TokenInfo
            pools: { id: string }[]
          }
          return {
            pools: tokenPools,
            tokens: [token],
            poolIds: tokenPools.map((p: { id: string }) => p.id),
            tokenIds: [token.id],
          }
        }
      }
    }

    return options
  }, [isArweaveAddress, exactMatch, options])

  // Group pools by token pair and include additional information.
  const groupedPools = useMemo(() => {
    const tokenInfoCache = $tokenInfoCache.get()
    // Each group will have: tokenIds, tokenTickers, pools, and totalLiquidity.
    const groups: {
      [key: string]: {
        tokenIds: string[]
        tokenTickers: string[]
        pools: Pool[]
        totalLiquidity: number
      }
    } = {}

    for (const pool of finalOptions.pools) {
      if (
        typeof pool !== "object" ||
        pool === null ||
        !("baseToken" in pool && "quoteToken" in pool && "id" in pool)
      )
        continue
      try {
        const baseToken: TokenInfo =
          pool.baseToken in PRE_CACHED_TOKENS
            ? PRE_CACHED_TOKENS[pool.baseToken]
            : JSON.parse(tokenInfoCache[pool.baseToken] as string)
        const quoteToken: TokenInfo =
          pool.quoteToken in PRE_CACHED_TOKENS
            ? PRE_CACHED_TOKENS[pool.quoteToken]
            : JSON.parse(tokenInfoCache[pool.quoteToken] as string)
        const sortedTickers = [baseToken.ticker, quoteToken.ticker].sort()
        const key = sortedTickers.join(" / ")
        if (!groups[key]) {
          groups[key] = {
            tokenIds: [baseToken.id, quoteToken.id],
            tokenTickers: sortedTickers,
            pools: [],
            totalLiquidity: 0,
          }
        }
        groups[key].pools.push(pool)
        // Get liquidity from the overview store.
        const poolOverview = poolsOverview?.[pool.id]
        const liquidity = poolOverview?.liquidityUsd ?? 0
        groups[key].totalLiquidity += liquidity
      } catch {
        // Ignore any errors in parsing token data.
      }
    }
    return groups
  }, [finalOptions.pools, poolsOverview])

  return { options: finalOptions, groupedPools, loading: searchLoading, exactMatch }
}
